/**
 * Helpers to convert a review report model into a service-contract.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "convertModelToServiceContract", {
    enumerable: true,
    get: function() {
        return convertModelToServiceContract;
    }
});
const convertModelToServiceContract = (reviewReportDocument)=>{
    if (!reviewReportDocument || typeof reviewReportDocument !== "object") {
        return reviewReportDocument;
    }
    // Initial values compatible with all types
    const { reviewReportType, ...baseReviewReport } = getBaseReviewReportProperties(reviewReportDocument);
    let reviewReport;
    // Type specific values
    if (reviewReportType === "external") {
        const externalAuthor = getReviewReportExternalAuthor(reviewReportDocument);
        const externalReviewReport = {
            ...baseReviewReport,
            reviewReportType,
            authors: [
                externalAuthor
            ],
            currentAuthor: externalAuthor
        };
        reviewReport = externalReviewReport;
    } else {
        const { authors } = reviewReportDocument;
        const { authorTurn } = baseReviewReport;
        const currentAuthor = authors.length > authorTurn ? authors[authorTurn] : null;
        if (reviewReportType === "easy-add") {
            if (!reviewReportDocument.easyAddData) {
                throw new Error(`Easy add review report with ID [${reviewReportDocument._id}] is missing easy_add_data`);
            }
            const easyAddReviewReport = {
                ...baseReviewReport,
                reviewReportType,
                approvalStatus: getReviewReportApprovalStatus(reviewReportDocument),
                addedBy: reviewReportDocument.easyAddData?.createdBy ?? null,
                approvedBy: reviewReportDocument.approval?.approvedBy ?? null,
                easyAddComment: reviewReportDocument.formComment,
                authors,
                currentAuthor,
                easyAddConfigId: reviewReportDocument.easyAddData?.easyAddConfig
            };
            reviewReport = easyAddReviewReport;
        } else {
            const standardReviewReport = {
                ...baseReviewReport,
                reviewReportType,
                authors,
                currentAuthor
            };
            reviewReport = standardReviewReport;
        }
    }
    return reviewReport;
};
const getBaseReviewReportProperties = (reviewReportDocument)=>{
    const easyAdd = reviewReportDocument.easyAdd ?? getReviewReportEasyAdd(reviewReportDocument);
    const external = reviewReportDocument.external ?? getReviewReportExternal(reviewReportDocument);
    const reviewReportType = easyAdd ? "easy-add" : external ? "external" : "standard";
    const status = reviewReportDocument.status ?? getReviewReportStatus(reviewReportDocument);
    const submit = reviewReportDocument.submit ?? getReviewReportSubmit(reviewReportDocument);
    const signed = reviewReportDocument.signed ?? getReviewReportSigned(reviewReportDocument);
    const closed = reviewReportDocument.closed ?? false;
    const completed = reviewReportDocument.completed ?? false;
    const completeDate = completed && reviewReportDocument.completeDate || null;
    const authorTurn = reviewReportDocument.authorTurn ?? 0;
    const { signers, signerTurn, currentSigner } = getReviewReportSignerProperties(reviewReportDocument);
    const participants = external ? [] : [
        ...reviewReportDocument.authors,
        ...reviewReportDocument.signers
    ];
    const baseReviewReport = {
        // Base properties
        _id: reviewReportDocument._id,
        company: reviewReportDocument.company,
        reviewParent: reviewReportDocument.reviewParent,
        subject: reviewReportDocument.subject,
        reviewFormId: reviewReportDocument.formId,
        // Configuration
        authorsDueDate: reviewReportDocument.authorsDueDate,
        closed,
        completed,
        completeDate,
        reviewReportType,
        redacted: reviewReportDocument.redacted ?? false,
        signed,
        signers: external ? [] : signers ?? [],
        submit,
        startDate: reviewReportDocument.startDate,
        signersDueDate: reviewReportDocument.signersDueDate,
        visible: reviewReportDocument.visible ?? false,
        // Metadata
        status,
        authorTurn,
        currentSigner: null,
        participants,
        // Calculated values
        lastAuthorChange: reviewReportDocument.lastAuthorChange,
        lastSignerChange: reviewReportDocument.lastSignerChange,
        lastSignOffComment: reviewReportDocument.signOffComment,
        lastSignOffDate: reviewReportDocument.signOffDate,
        lastSubmitDate: reviewReportDocument.submitDate,
        submitDates: reviewReportDocument.submitDates ?? {},
        signOffDates: reviewReportDocument.signOffDates ?? {},
        // Content (passed through for ReviewReportWithDefault, ignored by ReviewReportWithoutContent)
        answers: reviewReportDocument.answers ?? [],
        signOffComments: reviewReportDocument.signOffComments ?? {}
    };
    // Optional properties
    if (signerTurn != null) {
        baseReviewReport.signerTurn = signerTurn;
    }
    if (currentSigner != null) {
        baseReviewReport.currentSigner = currentSigner;
    }
    return baseReviewReport;
};
const getReviewReportSubmit = (reviewReport)=>{
    // Not null && not undefined
    if (reviewReport.submit != null) {
        return reviewReport.submit;
    }
    return false;
};
const getReviewReportSigned = (reviewReport)=>{
    // Not null && not undefined
    if (reviewReport.signed) {
        return reviewReport.signed;
    }
    return false;
};
const getReviewReportStatus = (reviewReport)=>{
    // Not null && not undefined
    if (reviewReport.status != null) {
        return reviewReport.status;
    }
    const { deleted, deletedAt, completed, closed } = reviewReport;
    if (deleted || deletedAt) {
        return "deleted";
    }
    const submit = reviewReport.submit ?? getReviewReportSubmit(reviewReport);
    const signed = reviewReport.signed ?? getReviewReportSigned(reviewReport);
    if (completed || submit && signed) {
        if (closed) {
            return "closed";
        }
        return "completed";
    }
    if (submit && !signed) {
        return "awaiting-signer";
    }
    return "awaiting-author";
};
const getReviewReportExternal = (reviewReport)=>{
    // Not null && not undefined
    if (reviewReport.external != null) {
        return reviewReport.external;
    }
    if (reviewReport.externalAuthor != null || reviewReport.externalAuthorEmail != null) {
        return true;
    }
    return false;
};
const getReviewReportExternalAuthor = (reviewReport)=>{
    const { externalAuthor, externalAuthorEmail, externalAuthorInvitation, lastInvite } = reviewReport;
    return {
        name: externalAuthor ?? null,
        email: externalAuthorEmail ?? null,
        message: externalAuthorInvitation ?? null,
        sentAt: lastInvite ?? null
    };
};
const getReviewReportSignerProperties = (reviewReport)=>{
    const { signers } = reviewReport;
    if (!Array.isArray(signers)) {
        return {};
    }
    const signerTurn = reviewReport.signerTurn ?? (Array.isArray(signers) ? 0 : undefined);
    const currentSigner = reviewReport.signer ?? (signerTurn != null && signers.length > signerTurn ? signers[signerTurn] : null);
    return {
        signers,
        signerTurn,
        currentSigner
    };
};
const getReviewReportEasyAdd = (reviewReport)=>{
    // Not null && not undefined
    if (reviewReport.easyAdd != null) {
        return reviewReport.easyAdd;
    }
    if (reviewReport.easyAddData != null) {
        return true;
    }
    return false;
};
const getReviewReportApprovalStatus = (reviewReport)=>{
    if (reviewReport.approval?.approvalStatus) {
        return reviewReport.approval.approvalStatus;
    }
    return "approved";
};
